import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { PeakRss } from "@/components/MemoryPanel";
import { CacheChip } from "@/components/CacheChip";
import { api } from "@/lib/api";
import { normalizeJobStatus } from "@/lib/jobStatus";
import { useAuthToken } from "@/lib/auth";
import {
  buildRows,
  parseCompileJobs,
  platformLabel,
  type BuildRow,
  type LiveJob,
} from "@/lib/buildLabels";
import { darwinRecordFor } from "@/lib/nativeTargets";
import { FailureChip } from "@/components/FailureChip";
import { shareMap } from "@/lib/quietRefresh";

const STATUS_VARIANT: Record<
  BuildRow["status"],
  "secondary" | "destructive" | "success" | "warning"
> = {
  queued: "secondary",
  running: "warning",
  succeeded: "success",
  failed: "destructive",
  cancelled: "secondary",
};

/** The darwin-arm64 follow-up job a version row's native_targets names. */
function darwinJobIdOf(
  nativeTargets: string | null | undefined,
  status?: string | null,
): string | null {
  // A record from a previous attempt (this one has not published yet) is not
  // this attempt's build.
  return darwinRecordFor(nativeTargets, status).current?.jobId ?? null;
}

/** The ordered builds of one publish attempt: position (n/total), what each
 *  is ("LLVM instrumented", "final (PGO)", "plain rebuild (PGO fallback)",
 *  "darwin arm64 follow-up", the two local training runs ...), platform/libc,
 *  status and compile time. Names and order come from the publisher
 *  (publish_versions.compile_jobs / the run's history entry); status and
 *  duration come from the live job rows (admin jobs list `ids=`), polled
 *  while the attempt is in progress. Renders nothing for an attempt from a
 *  publisher that predates the list. */
export function AttemptBuilds({
  compileJobs,
  nativeTargets,
  status,
  live,
}: {
  compileJobs: string | unknown[] | null | undefined;
  nativeTargets: string | null | undefined;
  status?: string | null;
  /** The attempt is still running (poll the job rows). */
  live: boolean;
}) {
  const token = useAuthToken();
  const records = useMemo(() => parseCompileJobs(compileJobs), [compileJobs]);
  const darwinJobId = darwinJobIdOf(nativeTargets, status);
  const ids = useMemo(() => {
    const out = records.flatMap((r) => (r.jobId ? [r.jobId] : []));
    if (darwinJobId) out.push(darwinJobId);
    return [...new Set(out)].sort();
  }, [records, darwinJobId]);
  const idsKey = ids.join(",");

  const [jobs, setJobs] = useState<ReadonlyMap<string, LiveJob>>(new Map());
  useEffect(() => {
    if (ids.length === 0) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = () => {
      api<{ ok: true; jobs: LiveJob[] }>(
        token,
        `/admin/jobs?ids=${encodeURIComponent(idsKey)}&limit=${Math.max(ids.length, 1)}`,
      )
        .then((r) => {
          if (cancelled) return;
          const m = new Map(
            r.jobs.map((j) => [j.id, normalizeJobStatus(j)] as const),
          );
          setJobs((prev) => shareMap(prev, m));
          const open = r.jobs.some(
            (j) => j.status === "queued" || j.status === "running",
          );
          if (live || open) timer = setTimeout(load, 5_000);
        })
        .catch(() => {
          // The recorded status/span still show; try again while live.
          if (!cancelled && live) timer = setTimeout(load, 10_000);
        });
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, idsKey, live]);

  const rows = buildRows(records, jobs, darwinJobId);
  if (rows.length === 0) return null;
  return (
    <div className="space-y-1" data-testid="attempt-builds">
      <div className="text-xs text-muted-foreground">
        Builds of this attempt ({rows.filter((r) => !r.local).length} compile
        jobs
        {rows.some((r) => r.local) ? ", plus local training runs" : ""})
      </div>
      <ul className="space-y-1">
        {rows.map((r, i) => (
          <li
            key={`${r.seq ?? "d"}:${r.jobId ?? i}`}
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
          >
            <span className="w-9 tabular-nums text-muted-foreground">
              {r.seqText ?? "—"}
            </span>
            <span className="font-medium">{r.label}</span>
            {r.local ? (
              <Badge variant="outline" className="font-normal">
                local run on the publish host
              </Badge>
            ) : (
              r.platform && (
                <Badge variant="outline" className="font-mono font-normal">
                  {platformLabel({ platform: r.platform })}
                </Badge>
              )
            )}
            <Badge variant={STATUS_VARIANT[r.status]}>
              {r.local && r.status === "running" ? "running" : r.status}
            </Badge>
            <span className="tabular-nums text-muted-foreground">
              {r.duration ?? ""}
            </span>
            {r.failure && (
              <FailureChip
                job={{ status: r.status, ...r.failure }}
                className="basis-full pl-11"
              />
            )}
            {r.jobId && !r.local && (
              <PeakRss
                job={{ ...(jobs.get(r.jobId) ?? {}), status: r.status }}
              />
            )}
            {r.jobId && !r.local && (
              <CacheChip
                job={{ ...(jobs.get(r.jobId) ?? {}), status: r.status }}
              />
            )}
            {r.jobId && (
              <Link
                to={`/logs/${encodeURIComponent(r.jobId)}`}
                className="font-mono underline decoration-dotted text-muted-foreground hover:text-foreground"
                title={`Open job ${r.jobId} (build log, flags, artifact download)`}
              >
                job {r.jobId.slice(0, 8)}
              </Link>
            )}
            {r.jobId && r.failure?.bundle_key && (
              <Link
                to={`/logs/${encodeURIComponent(r.jobId)}#failure`}
                className="underline decoration-dotted text-destructive hover:text-foreground"
                title="Open the job page: view or download the failure bundle (full build output, guest console and memory state, command lines)"
              >
                failure bundle
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
